import { call, put, select, takeLatest } from "redux-saga/effects";

import * as BotpressAPI from "../../data/api/omniBotpressAPI.js";
import * as OmniDataAPI from "../../data/omniDataAPI";
import { createOmniPaginationObject, isValidUUID } from "../../utils/helpers";
import * as Actions from "../redux/creatorSession";
import * as UserActions from "../redux/userSession";

const isBotpressOrganization = organizationODM => {
  return (
    organizationODM?.omniData?.organizationType === "botpress" ||
    organizationODM?.omniData?.type === "botpress" ||
    organizationODM?.organizationType === "botpress" ||
    organizationODM?.type === "botpress"
  );
};

const createFeedbackStatsRecord = (organizationODM, botpressBotODM = null) => {
  return {
    organizationId: organizationODM?._id,
    organizationName: organizationODM?.omniData?.name || organizationODM?.name || "Unknown",
    botIdentifier:
      botpressBotODM?.omniData?.name ||
      botpressBotODM?.omniData?.botId ||
      organizationODM?.omniData?.name ||
      organizationODM?.name ||
      "Unknown",
    newCount: 0,
    openCount: 0,
    closedCount: 0,
    retestCount: 0,
    blockedCount: 0,
    fixedInTestCount: 0,
    unknownCount: 0,
    totalCount: 0,
  };
};

const addFeedbackRecordToStats = (statsRecord, feedbackRecord) => {
  switch (feedbackRecord?.status) {
    case 1:
      statsRecord.newCount += 1;
      break;
    case 2:
      statsRecord.openCount += 1;
      break;
    case 3:
      statsRecord.closedCount += 1;
      break;
    case 4:
      statsRecord.retestCount += 1;
      break;
    case 5:
      statsRecord.blockedCount += 1;
      break;
    case 6:
      statsRecord.fixedInTestCount += 1;
      break;
    default:
      statsRecord.unknownCount += 1;
      break;
  }

  statsRecord.totalCount += 1;
};

const createAssessmentStatsRecord = (organizationODM, testBotODM = null) => {
  return {
    organizationId: organizationODM?._id,
    organizationName: organizationODM?.omniData?.name || organizationODM?.name || "Unknown",
    botIdentifier:
      testBotODM?.omniData?.name ||
      testBotODM?.omniData?.botId ||
      organizationODM?.omniData?.name ||
      organizationODM?.name ||
      "Unknown",
    newCount: 0,
    passCount: 0,
    failCount: 0,
    retestCount: 0,
    unknownCount: 0,
    totalCount: 0,
  };
};

const addAssessmentRecordToStats = (statsRecord, assessmentRecord) => {
  switch (assessmentRecord?.status) {
    case 0:
      statsRecord.newCount += 1;
      break;
    case 1:
      statsRecord.passCount += 1;
      break;
    case 2:
      statsRecord.failCount += 1;
      break;
    case 3:
      statsRecord.retestCount += 1;
      break;
    default:
      statsRecord.unknownCount += 1;
      break;
  }

  statsRecord.totalCount += 1;
};

// Same pick as the portal's PROD bot (userSession): the production bot, else the
// first bot that isn't the conversation-analysis bot, else the first bot.
const resolveProdBotODM = organizationODM => {
  const botpressBots = organizationODM?.omniData?.botpressBots;
  if (botpressBots == null || botpressBots.length === 0) {
    return null;
  }
  return (
    botpressBots.find(bot => bot?.omniData?.isProduction) ||
    botpressBots.find(bot => !bot?.omniData?.isConversationAnalytics) ||
    botpressBots[0]
  );
};

const resolveTestBotODM = organizationODM => {
  const botpressBots = organizationODM?.omniData?.botpressBots;
  if (botpressBots == null || botpressBots.length === 0) {
    return null;
  }
  const testBot = botpressBots.find(
    bot => !bot?.omniData?.isProduction && !bot?.omniData?.isConversationAnalytics,
  );
  return testBot || botpressBots[0];
};

export function* loadOrganizations({ payload }) {
  try {
    let organizationODM = yield call(OmniDataAPI.getAllOrganizations, payload);
    yield put(Actions.loadOrganizationsSuccess(organizationODM));
    yield put(Actions.loadOrganizationFeedbackStatsRequest(organizationODM));
    yield put(Actions.loadOrganizationAssessmentStatsRequest(organizationODM));
  } catch (error) {
    yield put(Actions.creatorSessionRequestFail(error));
  }
}

export function* loadOrganizationFeedbackStats({ payload }) {
  try {
    const organizationODM =
      payload != null ? payload : yield select(Actions.organizationODMsSelector);
    const organizationFeedbackStats = [];

    for (let i = 0; i < organizationODM.length; i++) {
      const organization = organizationODM[i];

      if (!isBotpressOrganization(organization)) {
        continue;
      }

      const botpressBotODM = resolveProdBotODM(organization);
      const statsRecord = createFeedbackStatsRecord(organization, botpressBotODM);

      if (botpressBotODM == null) {
        organizationFeedbackStats.push(statsRecord);
        continue;
      }

      let feedbackPage = createOmniPaginationObject(null, 50);
      feedbackPage.pageIndex = 0;
      feedbackPage.sort = { createdAt: "desc" };
      feedbackPage.botODM = botpressBotODM;

      let hasMore = true;

      for (; hasMore; feedbackPage.pageIndex++) {
        const feedbackResult = yield call(BotpressAPI.searchFeedback, feedbackPage);

        if (feedbackResult?.rows != null) {
          feedbackResult.rows.forEach(feedbackRecord => {
            addFeedbackRecordToStats(statsRecord, feedbackRecord);
          });
        }

        hasMore = feedbackResult?.hasMore === true;
      }

      organizationFeedbackStats.push(statsRecord);
    }

    yield put(Actions.loadOrganizationFeedbackStatsSuccess(organizationFeedbackStats));
  } catch (error) {
    yield put(Actions.creatorSessionRequestFail(error));
  }
}

export function* loadOrganizationAssessmentStats({ payload }) {
  try {
    const organizationODM =
      payload != null ? payload : yield select(Actions.organizationODMsSelector);
    const organizationAssessmentStats = [];

    for (let i = 0; i < organizationODM.length; i++) {
      const organization = organizationODM[i];

      if (!isBotpressOrganization(organization)) {
        continue;
      }

      const testBotODM = resolveTestBotODM(organization);
      const statsRecord = createAssessmentStatsRecord(organization, testBotODM);

      if (testBotODM == null) {
        organizationAssessmentStats.push(statsRecord);
        continue;
      }

      let assessmentPage = createOmniPaginationObject(null, 50);
      assessmentPage.pageIndex = 0;
      assessmentPage.sort = { createdAt: "desc" };
      assessmentPage.botODM = testBotODM;

      let hasMore = true;

      for (; hasMore; assessmentPage.pageIndex++) {
        const assessmentResult = yield call(BotpressAPI.searchAssessment, assessmentPage);

        if (assessmentResult?.rows != null) {
          assessmentResult.rows.forEach(assessmentRecord => {
            addAssessmentRecordToStats(statsRecord, assessmentRecord);
          });
        }

        hasMore = assessmentResult?.hasMore === true;
      }

      organizationAssessmentStats.push(statsRecord);
    }

    yield put(Actions.loadOrganizationAssessmentStatsSuccess(organizationAssessmentStats));
  } catch (error) {
    yield put(Actions.creatorSessionRequestFail(error));
  }
}
export function* updateOrganization({ payload }) {
  try {
    //payload is organizationODM

    const currentList = yield select(Actions.organizationODMsSelector);

    if (payload._id == null) {
      //CREATE
      let newOrganization = yield call(OmniDataAPI.createOrganization, payload);
      if (newOrganization == null) {
        yield put(Actions.creatorSessionRequestFail("OmniDataAPI.createOrganization FAILED"));
      } else {
        const newList =
          currentList == null ? [] : currentList.filter(obj => obj._id !== payload._id); //creates a copy of the list
        newList.push(newOrganization);
        yield put(Actions.updateOrganizationSuccess(newList));
      }
    } else if (isValidUUID(payload._id)) {
      // UPDATE
      let newOrganization = yield call(OmniDataAPI.updateOrganization, payload);
      if (newOrganization == null) {
        yield put(Actions.creatorSessionRequestFail("OmniDataAPI.updateOrganization FAILED"));
      } else {
        const newList =
          currentList == null ? [] : currentList.filter(obj => obj._id !== payload._id);
        newList.push(newOrganization);
        yield put(Actions.updateOrganizationSuccess(newList));

        yield put(UserActions.reflectUpdatedOrganizationODMRequest(newOrganization));
      }
    } else {
      yield put(Actions.creatorSessionRequestFail("Bad organizationODM"));
    }
  } catch (error) {
    yield put(Actions.creatorSessionRequestFail(error));
  }
}

export function* deleteOrganization({ payload }) {
  try {
    if (isValidUUID(payload)) {
      const organizations = yield select(Actions.organizationODMsSelector);
      const newOrganizations =
        organizations == null ? [] : organizations.filter(obj => obj._id !== payload);
      let organization = yield call(OmniDataAPI.deleteOrganization, payload);
      if (organization != null) {
        yield put(Actions.deleteOrganizationSuccess(newOrganizations));
      } else {
        yield put(Actions.creatorSessionRequestFail("Bad organizationODM"));
      }
    }

    yield put(Actions.creatorSessionRequestFail("deleteOrganization: invalid UUID()"));
  } catch (error) {
    yield put(Actions.creatorSessionRequestFail(error));
  }
}

export function* setSelectedOrganization({ payload }) {
  try {
    //Validate Object
    yield put(Actions.setSelectedOrganizationSuccess(payload));
  } catch (error) {
    yield put(Actions.creatorSessionRequestFail(error));
  }
}

//#endregion

//  saga
export default function* organizationDataSaga() {
  yield takeLatest(Actions.loadOrganizationsRequest, loadOrganizations);
  yield takeLatest(Actions.loadOrganizationFeedbackStatsRequest, loadOrganizationFeedbackStats);
  yield takeLatest(Actions.loadOrganizationAssessmentStatsRequest, loadOrganizationAssessmentStats);
  yield takeLatest(Actions.updateOrganizationRequest, updateOrganization);
  yield takeLatest(Actions.deleteOrganizationRequest, deleteOrganization);
  yield takeLatest(Actions.setSelectedOrganizationRequest, setSelectedOrganization);
}
